import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { betCodeAPI } from '../api/endpoints';
import { PLATFORMS, MARKETS, CONFIDENCE_LEVELS, GAME_TYPES } from '../utils/constants';
import { FaArrowLeft, FaUpload, FaTimes, FaPlus } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './CreateBetCode.css';

const CreateBetCode = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    code: '',
    platform: '',
    number_of_games: 1,
    market_type: 'Multiple',
    odds: '',
    stake: '',
    potential_win: '',
    win_rate: '',
    confidence: 'medium',
    game_type: 'football',
    is_private: false,
    image_data: null,
  });

  const isPunterPlus = user?.punter_plus || false;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.code || !formData.number_of_games || !formData.odds) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate private bet creation
    if (formData.is_private && !isPunterPlus) {
      toast.error('You need Punter+ subscription to create private bet codes');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        number_of_games: parseInt(formData.number_of_games),
        odds: parseFloat(formData.odds),
        stake: parseFloat(formData.stake) || 0,
        potential_win: parseFloat(formData.potential_win) || 0,
        win_rate: parseFloat(formData.win_rate) || 0,
      };

      // Handle image
      if (imageFile) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(imageFile);
        });
        submitData.image_data = base64;
      }

      const response = await betCodeAPI.create(submitData);
      toast.success('Bet code created successfully!');
      navigate(`/bet-codes/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create bet code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-bet-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="create-bet-card">
        <div className="card-header">
          <h1>Create Bet Code</h1>
          <p className="card-subtitle">Share your winning bet with the community</p>
        </div>

        <form onSubmit={handleSubmit} className="create-bet-form">
          {/* Title */}
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              placeholder="e.g., Weekend Mega Accumulator"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              placeholder="Describe your bet code..."
              value={formData.description}
              onChange={handleChange}
              rows={4}
            />
          </div>

          {/* Code */}
          <div className="form-group">
            <label>Bet Code *</label>
            <input
              type="text"
              name="code"
              placeholder="Enter your bet code"
              value={formData.code}
              onChange={handleChange}
              required
            />
          </div>

          {/* Platform & Game Type */}
          <div className="form-row">
            <div className="form-group">
              <label>Platform</label>
              <select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
              >
                <option value="">Select Platform</option>
                {PLATFORMS.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Game Type</label>
              <select
                name="game_type"
                value={formData.game_type}
                onChange={handleChange}
              >
                {GAME_TYPES.map(g => (
                  <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Number of Games & Market Type */}
          <div className="form-row">
            <div className="form-group">
              <label>Number of Games *</label>
              <input
                type="number"
                name="number_of_games"
                placeholder="e.g., 5"
                value={formData.number_of_games}
                onChange={handleChange}
                min="1"
                required
              />
            </div>
            <div className="form-group">
              <label>Market Type</label>
              <select
                name="market_type"
                value={formData.market_type}
                onChange={handleChange}
              >
                {MARKETS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Odds, Stake, Potential Win */}
          <div className="form-row">
            <div className="form-group">
              <label>Odds *</label>
              <input
                type="number"
                name="odds"
                placeholder="e.g., 5.50"
                value={formData.odds}
                onChange={handleChange}
                step="0.01"
                min="1.01"
                required
              />
            </div>
            <div className="form-group">
              <label>Stake ($XPUNT)</label>
              <input
                type="number"
                name="stake"
                placeholder="e.g., 10.00"
                value={formData.stake}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Potential Win ($XPUNT)</label>
              <input
                type="number"
                name="potential_win"
                placeholder="e.g., 55.00"
                value={formData.potential_win}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </div>
          </div>

          {/* Win Rate & Confidence */}
          <div className="form-row">
            <div className="form-group">
              <label>Win Rate (%)</label>
              <input
                type="number"
                name="win_rate"
                placeholder="e.g., 75.5"
                value={formData.win_rate}
                onChange={handleChange}
                step="0.1"
                min="0"
                max="100"
              />
            </div>
            <div className="form-group">
              <label>Confidence</label>
              <select
                name="confidence"
                value={formData.confidence}
                onChange={handleChange}
              >
                {CONFIDENCE_LEVELS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Image Upload */}
          <div className="form-group">
            <label>Image (Optional)</label>
            <div className="image-upload-area">
              {imagePreview ? (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <button type="button" className="remove-image" onClick={removeImage}>
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <label className="upload-label">
                  <FaUpload />
                  <span>Click to upload image</span>
                  <span className="upload-hint">PNG, JPG, GIF up to 5MB</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    hidden
                  />
                </label>
              )}
            </div>
          </div>

          {/* Private Bet (Punter+ only) */}
          {isPunterPlus && (
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_private"
                  checked={formData.is_private}
                  onChange={handleChange}
                />
                <span>Make this a private bet code</span>
                <span className="checkbox-hint">Only your subscribers can view this bet</span>
              </label>
            </div>
          )}

          {!isPunterPlus && (
            <div className="upgrade-notice">
              <span>🔒</span>
              <div>
                <strong>Upgrade to Punter+</strong>
                <p>Create private bet codes and earn from your subscribers</p>
                <button 
                  type="button" 
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate('/subscription')}
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="small" /> : 'Create Bet Code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBetCode;